import NumberInput from "@/app/components/ui/Inputs/NumberInput";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { IInvoiceProps, useCartInvoiceListAPI } from "@/app/hooks/api_hooks/Cart/useCartInvoiceListAPI";
import { usePaymentMethodListAPI } from "@/app/hooks/api_hooks/payment/usePaymentMethodListAPI";
import { usableRewardCredit } from "@/app/utils/rewardCredit";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import InvoiceCancellationModal from "../../WalletHistoryComponent/InvoiceCancellationModal";
import UrgentCancelItemsModal from "../../WalletHistoryComponent/UrgentCancelItemsModal";
import RemoveMembershipModal from "../../WalletHistoryComponent/RemoveMembershipModal";
import InvoiceReturnModalComponent from "../../WalletHistoryComponent/InvoiceReturnModalComponent";
import InvoiceRefundModalComponent from "../../WalletHistoryComponent/InvoiceRefundModalComponent";
import { ICartFilterBody, ICartUserInvoiceListResponseListData } from "@/api_framework/api_modals/Cart";
import { useCartListAPI } from "@/app/hooks/api_hooks/Cart/useCartListAPI";
import RBACFlag from "@/app/components/core/RBACFlag";
import PaymentConfirmationModal from "../PaymentConfirmationModal";
import SnapmintIntegration from "../SnapmintIntegration";
import { useSnapmintCancelAPI } from "@/app/hooks/api_hooks/Cart/useSnapmintCancelAPI";
import InvoiceReturnClinicModalComponent from "../../WalletHistoryComponent/InvoiceReturnClinicModal";
import { IPOSProps, localizeQRExpiry, useOnlinePaymentPostAPI } from "@/app/hooks/api_hooks/payment/useOnlinePaymentPostAPI";
import SwitchOnlineToOfflineConfirmationModal from "../SwitchOnlineToOfflineConfirmationModal";
import clsx from "clsx";
import { Switch } from "@headlessui/react";
import { useOnlinePaymentValidateAPI } from "@/app/hooks/api_hooks/payment/useOnlinePaymentValidateAPI";
import { useOnlinePaymentDeleteAPI } from "@/app/hooks/api_hooks/payment/useOnlinePaymentDeleteAPI";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import OnlinePaymentCancelModal from "../OnlinePaymentCancelModal";
import InvoiceCancelWindowGate from "../../InvoiceCancelWindowGate";
import { usePaymentOnlineToOfflineGetListAPI } from "@/app/hooks/api_hooks/payment/usePaymentOnlineToOfflineGetListAPI";
import MappedClinicAndCityPage from "@/app/routes/error_routes/MapClinicAndCityPage";
import FollowupBookingCreation from "../FollowupBookingCreation";
import { useNavigate } from "react-router";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { usePaymentWebSocket } from "@/app/hooks/api_hooks/payment/usePaymentWebSocket";
import { useSocket } from "@/app/components/socket/SocketProvider";
import toast from "react-hot-toast";
import { useSendBookingMirrorEventAPI } from "@/app/hooks/api_hooks/bookings/useSendBookingMirrorEventAPI";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useAdvanceCollectionEligibilityAPI } from "@/app/hooks/api_hooks/payment/useAdvanceCollectionEligibilityAPI";
import {
  isUrgentCancelAllowedUser,
  isWithinUrgentCancelWindow,
  isWithinVoucherPaidCancelWindow,
  isUrgentCancelEligibleStatus,
  getInvoiceCancelWindowSeconds,
} from "@/constants";
import { QR_TIMEOUT_SECONDS } from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/BillingSummaryComponent";
import { VMS_CLICK_EVENTS, VMS_SCREEN_IDS, useVMSClickLog } from "@/app/hooks/useVMSClickLog";

const CartInvoiceTableComponent = () => {
  const { getRouteKey } = useRouter();
  const { veticDateFormat, InvoicestatusBackgroundColor, InvoiceStatusDataTransformed } = useUtils();
  const { desktop } = useAppSelector((state) => state.root.UIStates.sidebar);
  const { forOnlyNumbersWithDecimal } = useFormValidations();
  const { NO_CART } = useImageConstants();
  const { inoviceList, walletDetails } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState
    .state as ICartState;
  // Reward credit counts as wallet here; PaymentConfirmationModal splits it out of used_amount.
  const [useRewardCredit, setUseRewardCredit] = useState<boolean>(true);
  const rewardCreditBalance = Number(walletDetails?.wallets?.marketing_credit || 0);
  const rewardCreditAvailable = usableRewardCredit(rewardCreditBalance, inoviceList ?? []);
  const rewardCreditUsable = useRewardCredit ? rewardCreditAvailable : 0;
  const usableWalletAmount = (walletDetails?.wallet_amount ?? 0) + rewardCreditUsable;
  const isCartInvoiceLoading = useLoader("isCustomerInvoiceTableInProgress");
  const { customerId, customerDetails, petId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;
  const navigate = useNavigate();
  const { execute: fetchPaymentMode, paymentMode, onlinePaymentMode, paymentMachine } = usePaymentMethodListAPI();
  const {
    execute: fetchInvoiceList,
    emi_meta_data,
    online_payment_details,
    cmbookingRecommendations,
  } = useCartInvoiceListAPI();
  const userLogInfo = useAppSelector((state) => state.auth?.user?.displayName);
  const isDogyWorld = userLogInfo && userLogInfo.toLowerCase().includes("edcpos");
  const { execute: fetchCartList } = useCartListAPI();
  const { execute: calcelSnapmint } = useSnapmintCancelAPI();
  const { execute: deleteOnlinePayment } = useOnlinePaymentDeleteAPI();
  const { execute: logClickAPI } = useClickLogAPI();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.CART_BILLING);
  const userEmail = useAppSelector((state) => state.auth?.user?.email);
  const { execute: collectOnlinePayment } = useOnlinePaymentPostAPI();
  const { execute: onlinePaymentValidate } = useOnlinePaymentValidateAPI();
  const { sendJsonMessage } = useSocket();
  const { execute: fetchOfflinereason } = usePaymentOnlineToOfflineGetListAPI();
  const { execute: fetchAdvanceCollectionEligibility, isEligible: isAdvanceCollectionEligible } =
    useAdvanceCollectionEligibilityAPI();
  const [paymentConfirmation, setPaymentConfirmation] = useState<boolean>(false);
  const [initialWalletAmount, setInitialWalletAmount] = useState<boolean>(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [returnState, setReturnState] = useState(false);
  const [refundState, setRefundState] = useState(false);
  const [invoiceType, setInvoiceType] = useState(null);
  const [isSnapmintValue, setIsSnapmintValue] = useState(0);
  const [isSnapmintButton, setIsSnapmintValueButton] = useState(0);
  const [paidSnapmintInvoices, setPaidSnapmintInvoices] = useState(null);
  const [cancelState, setCancelState] = useState(false); // For Cancel modal
  const [isUrgentCancel, setIsUrgentCancel] = useState(false); // Urgent-cancel mode for the cancel modal
  const [urgentCancelItemsState, setUrgentCancelItemsState] = useState(false); // Item-list modal for urgent cancel
  // Clinic of the invoice being urgent-cancelled, kept apart from the return /
  // cancel clinic prompt: urgent cancel never asks the user to switch clinic.
  const [urgentCancelClinic, setUrgentCancelClinic] = useState<any>(null);
  const [cancelClinicState, setCancelClinicState] = useState(false);
  const [removeMembershipInvoice, setRemoveMembershipInvoice] = useState<ICartUserInvoiceListResponseListData | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [snapmintorder_status, setSnapmintorder_status] = useState(null);
  const [cancelPaymentInProgress, setCancelPaymentInProgress] = useState(false);
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const [buttonSource, setButtonSource] = useState(null);
  const [initialOffline, setInitialOffline] = useState(false);
  const [isOfflinePaymentEnabled, setIsOfflinePaymentEnabled] = useState(false);
  const [isOfflineConfirmationModal, setIsOfflineConfirmationModal] = useState(false);
  const [onlinePaymentOrderPending, setOnlinePaymentOrderPending] = useState(false);
  const [cancelOnlinePaymentInProgress, setCancelOnlinePaymentInProgress] = useState(false);
  const [onlinePaymentvalidation, setOnlinePaymentvalidation] = useState(false);
  const [initialpaymentDetails, setInitialpaymentDetails] = useState(false);
  const [offlineData, setOfflineData] = useState([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [isVisibleMappedClinic, setIsVisibleMappedClinic] = useState<boolean>(false);
  const [defaultMachineId, setDefaultMachineId] = useState(null);
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState<boolean>(false);
  const [hasAutoOpenedFollowUp, setHasAutoOpenedFollowUp] = useState<boolean>(false);
  const [qrExpiresAt, setQrExpiresAt] = useState<number | null>(null);
  const [qrSecondsLeft, setQrSecondsLeft] = useState(0);
  const { execute: sendMirrorEvent } = useSendBookingMirrorEventAPI();

  useLayoutEffect(() => {
    if (!qrExpiresAt) return;
    const tick = () => setQrSecondsLeft(Math.max(0, Math.floor((qrExpiresAt - Date.now()) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [qrExpiresAt]);

  const redirectToInvoiceList = () => {
    navigate(`/app/customer/profile/customer-details/invoice/${customerId}/${petId}`);
  };

  const handlePaymentSuccess = () => {
    setPaymentConfirmation(false);
    logVMSClick(VMS_CLICK_EVENTS.PAYMENT_SUCCESS, "Payment Recorded", { client_id: customerId, pet_id: petId });
    if ((cmbookingRecommendations?.length ?? 0) > 0) {
      setIsFollowUpModalOpen(true);
    } else {
      sendMirrorEvent("REMINDERS", { vms_user_id: userEmail, customer_id: customerId });
      logVMSClick(VMS_CLICK_EVENTS.REMINDERS_SENT, "Reminders", { event_action: "PAYMENT_RECORDED", client_id: customerId });
      redirectToInvoiceList();
    }
  };

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      payments: [],
      offline_payments: [],
      used_amount: 0,
    },
  });

  const { control, handleSubmit, formState } = formHook;
  const {
    fields: paymentFields,
    append: addOnlinePaymentMethod,
    remove: removePaymentMethod,
    update: updateOnlinePaymentMethod,
    replace: replaceOnlinePaymentMethod,
  } = useFieldArray({
    control,
    name: "payments",
  });
  const {
    fields: offlinePaymentFields,
    append: addOfflinePaymentMethod,
    remove: removeOfflinePaymentMethod,
    replace: replaceOffilePaymentMethod,
  } = useFieldArray({
    control,
    name: "offline_payments",
  });
  const { fields: invoiceField, replace: updatedInvoice } = useFieldArray({
    control,
    name: "invoices",
  });

  const isUPIorCard = (paymentMode: string) => {
    return paymentMode === "UPI" || paymentMode === "Card";
  };

  useEffect(() => {
    fetchPaymentMode();
    fetchOfflinereason().then(({ status, message }) => {
      if (status) {
        setOfflineData(message);
      }
    });
  }, []);

  useEffect(() => {
    if (customerId) {
      const constructedBody: IInvoiceProps = {
        user_id: customerId,
        start_date: null,
        end_date: null,
        statuses: [],
        logged_in_clinic_id: mappedClinicId,
      };
      fetchInvoiceList(constructedBody, "payment_page");
      const filterBody: ICartFilterBody = {
        cart_status: ["Unbilled"],
        start_date: null,
        end_date: null,
      };
      fetchCartList(filterBody, customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId && mappedClinicId) {
      fetchAdvanceCollectionEligibility(mappedClinicId, customerId, "payment");
    }
  }, [customerId, mappedClinicId]);

  //Auto-open FollowupBookingCreation once invoice API and cmbookingRecommendations are loaded
  //and there is no invoice / nothing to collect
  useEffect(() => {
    if (
      !isCartInvoiceLoading &&
      !hasAutoOpenedFollowUp &&
      (inoviceList?.length ?? 0) === 0 &&
      (cmbookingRecommendations?.length ?? 0) > 0
    ) {
      setHasAutoOpenedFollowUp(true);
      const logBody: IClickLogBody = {
        event_type: "RECORD_PAYMENT_NO_INVOICE_AUTO_OPEN",
        scr_id: "CART_INVOICE_PAYMENT_PAGE",
        client_id: customerId || "",
        pet_id: petId || undefined,
        clinic_id: mappedClinicId || undefined,
        clinic_name: mappedClinicName || undefined,
        vms_user_id: userEmail || "",
        source: "EMR_FOLLOW_UP",
        timestamp: Date.now(),
      };
      setTimeout(() => {
        logClickAPI(logBody);
      }, 0);
      setIsFollowUpModalOpen(true);
    }
  }, [isCartInvoiceLoading, inoviceList, cmbookingRecommendations, hasAutoOpenedFollowUp]);

  const paymentMethodItems = useSelectMenuReducer(paymentMode, "payment_mode", "payment_mode");
  const onlinePaymentModeItems = useSelectMenuReducer(onlinePaymentMode, "payment_mode", "payment_mode");
  const paymentMachineItems = useSelectMenuReducer(paymentMachine, "machine_value", "machine_id");

  useEffect(() => {
    if (paymentMachine?.length > 0) {
      setDefaultMachineId(paymentMachine[0]?.machine_id);
    }
  }, [paymentMachine]);

  const restrictedTitles = [
    "Bad Debt",
    "CX Credit",
    "VBCredit",
    "QComm COD", // Note: it's not "QComm_COD", make sure the title matches
    "Payu",
    "Razor Pay",
    "Cheque",
    "ShopSe",
    "Blue7Vets Credit",
  ];

  // RBAC: BILLING_RESTRICTED_PAYMENT_METHODS — legacy roles: central_ops, central_ops_admin, super_admin
  const canUseRestrictedPaymentMethods = useFeatureAccess("BILLING_RESTRICTED_PAYMENT_METHODS");

  const visiblePaymentOptions = paymentMethodItems.filter(
    (item) => !restrictedTitles.includes(item.title) || canUseRestrictedPaymentMethods,
  );

  useEffect(() => {
    if (inoviceList?.length > 0) {
      const currentInvoices = formHook.getValues("invoices") || [];
      const isFirstLoad = currentInvoices.length === 0;
      const unpaidInvoices = inoviceList.filter((item) => item?.due_amount > 0 && item?.status !== "paid");
      if (unpaidInvoices.length === 0) {
        updatedInvoice([]);
        return;
      }
      const uniqueInvoices = unpaidInvoices.map((item) => {
        if (isFirstLoad) {
          return { ...item, amount_to_be_paid: item?.due_amount || 0 };
        }
        const existing = currentInvoices.find((inv: any) => inv.invoice_id === item.invoice_id);
        const existingAmount = existing ? parseFloat(existing.amount_to_be_paid) || 0 : item?.due_amount || 0;
        return {
          ...item,
          amount_to_be_paid: Math.min(item?.due_amount || 0, existingAmount),
        };
      });
      updatedInvoice(uniqueInvoices);
    } else {
      updatedInvoice([]);
    }
  }, [inoviceList]);

  useEffect(() => {
    if (online_payment_details?.length > 0 && !initialpaymentDetails) {
      const paymentDetails = online_payment_details.map(localizeQRExpiry);
      replaceOnlinePaymentMethod(paymentDetails);
      const pendingQR = paymentDetails.find(
        (item) => item?.payment_mode === "QR_PAYMENT" && item?.order_status === "PENDING" && item?.image_base64,
      );
      if (pendingQR && isQRLive(pendingQR)) showQROnMirror(pendingQR);
      else if (pendingQR?.expires_at) setQrExpiresAt(pendingQR.expires_at * 1000);
      setInitialpaymentDetails(true);
    }
    if (!online_payment_details?.length && onlinePaymentMode?.length > 0 && !initialpaymentDetails) {
      replaceOnlinePaymentMethod([
        {
          payment_mode: onlinePaymentMode?.some((item) => item?.payment_mode === "QR_PAYMENT") ? "QR_PAYMENT" : null,
          amount: "0",
          order_status: "INITIATED",
          mobile_number: customerDetails?.mobile,
          machine_id: defaultMachineId,
        },
      ]);
    }
  }, [online_payment_details, initialpaymentDetails, defaultMachineId, onlinePaymentMode]);

  useEffect(() => {
    if (onlinePaymentModeItems?.length === 0 && paymentMethodItems?.length > 0 && !initialOffline) {
      replaceOffilePaymentMethod([{ payment_mode: null, amount: "0" }]);
      setInitialOffline(true);
      setIsOfflinePaymentEnabled(true);
    }
  }, [onlinePaymentModeItems, paymentMethodItems, initialOffline]);

  useEffect(() => {
    if (inoviceList?.length > 0 && walletDetails) {
      const totals = inoviceList.reduce(
        (acc, item) => {
          acc.totalInvoice += item?.invoice_total ?? 0;
          acc.totalDue += item?.due_amount ?? 0;
          return acc;
        },
        {
          totalInvoice: 0,
          totalDue: 0,
        },
      );
      const wallet_amount = usableWalletAmount;
      const isPaymentMethodAvailable = formHook.watch(`payments`)?.length === 1;
      const usedWalletAmount = parseFloat(formHook.watch("used_amount")) ?? 0;
      //without online paymet
      if (onlinePaymentModeItems?.length === 0 && paymentMethodItems?.length > 0) {
        if (wallet_amount === 0 || emi_meta_data?.snapmint_total_eligible_amount === totals.totalDue) {
          // formHook.setValue(`used_amount`, 0);
          const value = (totals.totalDue - isSnapmintValue - usedWalletAmount).toString();
          // formHook.setValue(`offline_payments.0.amount`, parseFloat(value) > 0 ? value : "0");
        } else if (wallet_amount >= totals.totalDue) {
          // formHook.setValue(`used_amount`, totals.totalDue);
        } else {
          const remAmount = totals.totalDue - usedWalletAmount - isSnapmintValue;
          const value = remAmount.toString();
          // formHook.setValue(`offline_payments.0.amount`, parseFloat(value) > 0 ? value : "0");
        }
        //with online payment
      } else {
        const isPaymentIitiate =
          formHook.watch(`payments`)?.length > 0 && formHook.watch(`payments`)[0]?.order_status === "INITIATED";
        if (wallet_amount === 0 || emi_meta_data?.snapmint_total_eligible_amount === totals.totalDue) {
          const value = (totals.totalDue - isSnapmintValue - usedWalletAmount).toString();
          isPaymentMethodAvailable &&
            isPaymentIitiate &&
            formHook.setValue(`payments.0.amount`, parseFloat(value) > 0 ? value : "0");
        } else if (wallet_amount >= totals.totalDue) {
          isPaymentIitiate && formHook.setValue(`payments.0.amount`, "0");
        } else {
          const remAmount = totals.totalDue - usedWalletAmount;
          const value = (remAmount - isSnapmintValue).toString();
          isPaymentMethodAvailable &&
            isPaymentIitiate &&
            formHook.setValue(`payments.0.amount`, parseFloat(value) > 0 ? value : "0");
        }
      }
    }
  }, [inoviceList, walletDetails, isSnapmintValue, formHook.watch("used_amount"), paymentFields?.[0]?.id]);

  //Prefilled wallet details
  useEffect(() => {
    if (inoviceList?.length > 0 && walletDetails && !initialWalletAmount) {
      const totals = inoviceList.reduce(
        (acc, item) => {
          acc.totalInvoice += item?.invoice_total ?? 0;
          acc.totalDue += item?.due_amount ?? 0;
          return acc;
        },
        {
          totalInvoice: 0,
          totalDue: 0,
        },
      );
      const wallet_amount = usableWalletAmount;
      if (wallet_amount === 0) {
        formHook.setValue(`used_amount`, 0);
      } else if (wallet_amount >= totals.totalDue) {
        formHook.setValue(`used_amount`, totals.totalDue);
      } else {
        formHook.setValue(`used_amount`, wallet_amount);
      }
      setInitialWalletAmount(true);
      const usedWalletAmount = parseFloat(formHook.watch("used_amount")) ?? 0;
      if (onlinePaymentModeItems?.length === 0 && paymentMethodItems?.length > 0) {
        if (wallet_amount === 0 || emi_meta_data?.snapmint_total_eligible_amount === totals.totalDue) {
          formHook.setValue(`used_amount`, 0);

          const value = (totals.totalDue - isSnapmintValue - usedWalletAmount).toString();
          formHook.setValue(`offline_payments.0.amount`, parseFloat(value) > 0 ? value : "0");
        }
      }
    }
  }, [inoviceList, walletDetails, initialWalletAmount]);

  const totalInvoiceAmount = () => {
    const totals = inoviceList?.reduce(
      (acc, item) => {
        ((acc.totalInvoice += item?.invoice_total ?? 0), (acc.totalDue += item?.due_amount ?? 0));
        return acc;
      },
      {
        totalInvoice: 0,
        totalDue: 0,
      },
    );
    return totals;
  };

  const totalInvoiceAmountToPaid = () => {
    const totals = formHook.watch("invoices")?.reduce(
      (acc, item) => {
        acc.totalToBePaid += parseFloat(item?.amount_to_be_paid) || 0;
        return acc;
      },
      {
        totalToBePaid: 0,
      },
    );
    return totals;
  };

  const totalAmountTopaid = () => {
    const usedAmount = parseFloat(formHook.watch("used_amount")) || 0; // Default to 0 if undefined or NaN
    const onlinePayments = formHook.watch("payments") || []; // Default to empty array if payments is undefined
    const offlinePayments = formHook.watch("offline_payments") || []; // Default to empty array if payments is undefined
    const totalsOnline = onlinePayments.reduce((acc, item) => {
      return acc + (parseFloat(item?.amount) || 0); // Parse item amount or default to 0
    }, 0);
    const totalOffline =
      offlinePayments?.length > 0 &&
      offlinePayments?.reduce((acc, item) => {
        return acc + (parseFloat(item?.amount) || 0);
      }, 0);
    const totalAmount = totalsOnline + usedAmount + (parseFloat(isSnapmintValue?.toString()) || 0);
    const result = isOfflinePaymentEnabled
      ? totalAmount + (totalOffline || 0)
      : onlinePaymentModeItems?.length === 0
        ? totalAmount + (totalOffline || 0)
        : totalAmount;
    return Number(result.toFixed(2));
  };

  const totalAmountRemaning = () => {
    const usedAmount = parseFloat(formHook.watch("used_amount")) || 0; // Default to 0 if undefined or NaN
    const onlinePayments = formHook.watch("payments") || []; // Default to empty array if payments is undefined
    const offlinePayments = formHook.watch("offline_payments") || []; // Default to empty array if payments is undefined
    const totalsOnlinePayments = onlinePayments?.reduce((acc, item) => {
      return acc + (parseFloat(item?.amount) || 0); // Parse item amount or default to 0
    }, 0);
    const totalsOfflinelinePayments = offlinePayments.reduce((acc, item) => {
      return acc + (parseFloat(item?.amount) || 0); // Parse item amount or default to 0
    }, 0);
    const totalInvoice = formHook.watch("invoices")?.reduce(
      (acc, item) => {
        acc.totalToBePaid += parseFloat(item?.amount_to_be_paid) || 0;
        return acc;
      },
      {
        totalToBePaid: 0,
      },
    );
    const Remaining_Amount =
      (totalInvoice?.totalToBePaid || 0) - (totalsOnlinePayments + usedAmount) - (parseFloat(isSnapmintValue?.toString()) || 0);
    const result = isOfflinePaymentEnabled ? Remaining_Amount - totalsOfflinelinePayments : Remaining_Amount;
    return Number(result.toFixed(2));
  };


  const handelRowAdd = (isAdd) => {
    const Remaining_Amount = totalAmountRemaning()?.toFixed();
    if (parseInt(Remaining_Amount) > 0 && isAdd === "ROWADD") {
      addOnlinePaymentMethod({
        payments_method: null,
        amount: Remaining_Amount,
        order_status: "INITIATED",
        order_id: null,
        mobile_number: customerDetails?.mobile,
        machine_id: defaultMachineId,
      });
    }
    if (isAdd === "ROW") {
      if (parseInt(Remaining_Amount) > 0) {
        addOnlinePaymentMethod({
          payments_method: null,
          amount: Remaining_Amount,
          order_status: "INITIATED",
          order_id: null,
          mobile_number: customerDetails?.mobile,
          machine_id: defaultMachineId,
        });
      } else {
        addOnlinePaymentMethod({
          payments_method: null,
          amount: "0",
          order_status: "INITIATED",
          order_id: null,
          mobile_number: customerDetails?.mobile,
          machine_id: defaultMachineId,
        });
      }
    }
  };

  const removeSnapmintPaymentMethod = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCancelPaymentInProgress(true);
    calcelSnapmint(orderId, customerId).then(({ status }) => {
      if (status) {
        setCancelPaymentInProgress(false);
        setIsSnapmintValue(0);
        setIsSnapmintValueButton(0);
        setSnapmintorder_status("cancel");
        if (onlinePaymentModeItems?.length === 0) {
          const paymentOptionValue = Number(formHook.watch(`offline_payments.0.amount`)); // convert to number
          const newAmount = paymentOptionValue + isSnapmintValue; //
          formHook.setValue(`offline_payments.0.amount`, newAmount.toString());
        } else {
          const isPaymentIitiate =
            formHook.watch(`payments`)?.length > 0 && formHook.watch(`payments`)[0]?.order_status === "INITIATED";
          const paymentOptionValue = Number(formHook.watch(`payments.0.amount`)); // convert to number
          const newAmount = paymentOptionValue + isSnapmintValue;
          isPaymentIitiate && formHook.setValue(`payments.0.amount`, newAmount.toString());
        }
        totalAmountTopaid();
        totalAmountRemaning();
      } else {
        setCancelPaymentInProgress(false);
      }
    });
  };

  const handelOnlinePaymentValidate = (index: any, order_id: any) => {
    setOnlinePaymentvalidation(true);
    const paymentMode = formHook.getValues(`payments.${index}.payment_mode`);
    const amount = formHook.getValues(`payments.${index}.amount`);
    const machine_id = formHook.getValues(`payments.${index}.machine_id`);
    logVMSClick(VMS_CLICK_EVENTS.PAYMENT_VALIDATE_CLICK, "Validate Payment", {
      event_action: paymentMode,
      client_id: customerId,
      order_id,
      amount: parseFloat(amount),
    });
    onlinePaymentValidate(order_id).then(({ status, message }) => {
      if (status) {
        updateOnlinePaymentMethod(index, {
          payment_mode: paymentMode,
          amount: amount,
          order_status: message,
          order_id: order_id,
          machine_id: machine_id,
        });
        setOnlinePaymentvalidation(false);
        if (paymentMode === "QR_PAYMENT" && ["completed", "failed"].includes(message?.toLowerCase())) {
          sendPaymentQREvent(message.toLowerCase() === "completed" ? "PAYMENT_SUCCESS" : "PAYMENT_FAILED", {
            order_id,
            amount: parseFloat(amount),
          });
        }
        if (message?.toLowerCase() === "failed") {
          updateOnlinePaymentMethod(index, {
            payment_mode: paymentMode,
            amount: "0",
            order_status: message,
            order_id: order_id,
            machine_id: machine_id,
          });
        }
      } else {
        setOnlinePaymentvalidation(false);
      }
    });
  };

  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activePollingOrderRef = useRef<string | null>(null);
  const retryCountRef = useRef<number>(0);
  const MAX_RETRIES = 50;
  const hasSettledRef = useRef(false);

  const refetchInvoicesAfterSettle = () => {
    if (hasSettledRef.current) return;
    hasSettledRef.current = true;
    // 1s delay to let backend complete settlement
    setTimeout(() => {
      if (customerId) {
        const constructedBody: IInvoiceProps = {
          user_id: customerId,
          start_date: null,
          end_date: null,
          statuses: [],
          logged_in_clinic_id: mappedClinicId,
        };
        fetchInvoiceList(constructedBody, "payment_page");
      }
    }, 1000);
  };

  // WebSocket-based payment status tracking
  const { connect: wsConnect, disconnect: wsDisconnect } = usePaymentWebSocket({
    onCompleted: (msg) => {
      if (wsTimeoutRef.current) {
        clearTimeout(wsTimeoutRef.current);
        wsTimeoutRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      const index = formHook.getValues("payments")?.findIndex((p: any) => p.order_id === msg.order_id);
      if (index >= 0) {
        const field = formHook.getValues(`payments.${index}`);
        updateOnlinePaymentMethod(index, {
          payment_mode: field.payment_mode,
          amount: field.amount,
          order_status: "COMPLETED",
          order_id: msg.order_id,
          mobile_number: field.mobile_number,
          machine_id: field.machine_id,
        });
        setOnlinePaymentvalidation(false);
        if (field.payment_mode === "QR_PAYMENT") {
          sendPaymentQREvent("PAYMENT_SUCCESS", { order_id: msg.order_id, amount: parseFloat(field.amount) });
        }
      }
      const discount = msg.data?.instant_discount_details;
      if (discount) {
        const saved = (discount.InstantDiscount / 100).toFixed(2);
        toast.success(`🎉 Offer applied! You saved ₹${saved}`, { duration: 5000 });
      }
      // Trigger settlement via GET, then refetch once
      onlinePaymentValidate(msg.order_id).then(() => refetchInvoicesAfterSettle());
    },
    onFailed: (msg) => {
      if (wsTimeoutRef.current) {
        clearTimeout(wsTimeoutRef.current);
        wsTimeoutRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      const index = formHook.getValues("payments")?.findIndex((p: any) => p.order_id === msg.order_id);
      if (index >= 0) {
        const field = formHook.getValues(`payments.${index}`);
        updateOnlinePaymentMethod(index, {
          payment_mode: field.payment_mode,
          amount: "0",
          order_status: "FAILED",
          order_id: msg.order_id,
          mobile_number: field.mobile_number,
          machine_id: field.machine_id,
        });
        if (field.payment_mode === "QR_PAYMENT") {
          sendPaymentQREvent("PAYMENT_FAILED", { order_id: msg.order_id });
        }
      }
    },
  });

  // Fallback polling (used when WebSocket fails to deliver within timeout)
  const pollPaymentStatus = (
    order_id: string,
    index: number,
    amount: string,
    paymentMode: string,
    mobile_number: string,
    machine_id: string,
  ) => {
    if (hasSettledRef.current) return;
    onlinePaymentValidate(order_id).then(({ status, message }) => {
      const normalizedMsg = message?.trim().toLowerCase();
      if (status) {
        if (normalizedMsg === "completed") {
          updateOnlinePaymentMethod(index, {
            payment_mode: paymentMode,
            amount,
            order_status: "COMPLETED",
            order_id,
            mobile_number,
            machine_id,
          });
          setOnlinePaymentvalidation(false);
          if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
          activePollingOrderRef.current = null;
          retryCountRef.current = 0;
          if (paymentMode === "QR_PAYMENT") {
            sendPaymentQREvent("PAYMENT_SUCCESS", { order_id, amount: parseFloat(amount) });
          }
          refetchInvoicesAfterSettle();
          return;
        }
        if (normalizedMsg === "failed") {
          updateOnlinePaymentMethod(index, {
            payment_mode: paymentMode,
            amount: "0",
            order_status: "FAILED",
            order_id,
            mobile_number,
            machine_id,
          });
          if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
          activePollingOrderRef.current = null;
          retryCountRef.current = 0;
          if (paymentMode === "QR_PAYMENT") {
            sendPaymentQREvent("PAYMENT_FAILED", { order_id });
          }
          return;
        }
      } else {
        setOnlinePaymentvalidation(false);
      }
      if (retryCountRef.current >= MAX_RETRIES) {
        if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
        activePollingOrderRef.current = null;
        retryCountRef.current = 0;
        return;
      }
      retryCountRef.current += 1;
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = setTimeout(() => {
        pollPaymentStatus(order_id, index, amount, paymentMode, mobile_number, machine_id);
      }, 4000);
    });
  };

  // Start tracking: WebSocket first, polling fallback after 45s
  const wsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startPaymentTracking = (
    order_id: string,
    index: number,
    amount: string,
    paymentMode: string,
    mobile_number: string,
    machine_id: string,
  ) => {
    hasSettledRef.current = false;
    try {
      wsConnect(order_id);
      wsTimeoutRef.current = setTimeout(() => {
        if (!hasSettledRef.current) {
          pollPaymentStatus(order_id, index, amount, paymentMode, mobile_number, machine_id);
        }
      }, 45000);
    } catch {
      pollPaymentStatus(order_id, index, amount, paymentMode, mobile_number, machine_id);
    }
  };

  const didMountRef = useRef(false);

  useEffect(() => {
    if (!didMountRef.current) {
      if (paymentFields?.length > 0) {
        const pendingOrders = paymentFields.filter((order) => order?.order_status === "PENDING");
        const pendingIndex = paymentFields.findIndex((order) => order?.order_status === "PENDING");

        if (pendingOrders.length > 0) {
          const order = pendingOrders[0];
          startPaymentTracking(
            order.order_id,
            pendingIndex,
            String(order.amount),
            order.payment_mode,
            order.mobile_number,
            order.machine_id,
          );
          didMountRef.current = true;
        }
      }
    }
    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      if (wsTimeoutRef.current) clearTimeout(wsTimeoutRef.current);
      wsDisconnect();
    };
  }, [paymentFields]);

  const sendPaymentQREvent = (action: string, payload: Record<string, any> = {}) => {
    sendJsonMessage({ event_type: "payment_qr", action, ...payload });
    logVMSClick(VMS_CLICK_EVENTS.QR_MIRROR_EVENT_SENT, action, {
      event_action: action,
      client_id: customerId,
      order_id: payload.order_id,
      amount: payload.amount,
    });
    setQrExpiresAt(action === "QR_SHOW" ? (payload.expires_at ? payload.expires_at * 1000 : Date.now() + QR_TIMEOUT_SECONDS * 1000) : null);
    if (action === "QR_SHOW") {
      localStorage.removeItem("lastPresentedCartId");
      localStorage.setItem("mirrorQROrderId", payload.order_id);
    }
    if (action !== "PAYMENT_SUCCESS") return;
    setTimeout(() => {
      if ((cmbookingRecommendations?.length ?? 0) > 0) setIsFollowUpModalOpen(true);
      else {
        sendMirrorEvent("REMINDERS", { vms_user_id: userEmail, customer_id: customerId });
        logVMSClick(VMS_CLICK_EVENTS.REMINDERS_SENT, "Reminders", { event_action: "QR_PAYMENT_SUCCESS", client_id: customerId });
      }
    }, 5000);
  };

  const isQRLive = (item: any) =>
    item?.payment_mode === "QR_PAYMENT" &&
    item?.order_status?.toLowerCase() === "pending" &&
    !!item?.image_base64 &&
    (!item?.expires_at || item.expires_at * 1000 > Date.now());

  const showQROnMirror = (item: any) =>
    sendPaymentQREvent("QR_SHOW", {
      customer_id: customerId,
      clinic_id: mappedClinicId,
      order_id: item.order_id,
      amount: parseFloat(item.amount),
      image_base64: item.image_base64,
      expires_at: item.expires_at,
      expires_in: item.expires_at ? Math.round(item.expires_at - Date.now() / 1000) : undefined,
    });

  useEffect(() => {
    const handleFocus = () => {
      const pendingQR = (formHook.getValues("payments") || []).find(isQRLive);
      if (!pendingQR) return;
      const mirrorTaken =
        !!localStorage.getItem("lastPresentedCartId") || localStorage.getItem("mirrorQROrderId") !== pendingQR.order_id;
      if (mirrorTaken) showQROnMirror(pendingQR);
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [customerId, mappedClinicId, sendJsonMessage]);

  //online payment POS
  const handelCollectPaymentOnline = (index: any) => {
    setOnlinePaymentOrderPending(true);
    setSelectedPaymentId(index);
    const paymentMode = formHook.getValues(`payments.${index}.payment_mode`);
    const currentInvoices = formHook.getValues("invoices") || [];
    const invoice_ids =
      paymentMode === "QR_PAYMENT"
        ? currentInvoices.map((item: any) => ({ invoice_id: item.invoice_id, amount: parseFloat(item.amount_to_be_paid) || 0 }))
        : inoviceList?.map((item) => item?.invoice_id);
    const mobile_number = formHook.getValues(`payments.${index}.mobile_number`);
    const machineId = formHook.getValues(`payments.${index}.machine_id`);
    const amount = formHook.getValues(`payments.${index}.amount`);
    const prefilledOrderId = formHook.getValues(`payments.${index}.order_id`);
    const prefilledQR = formHook.getValues(`payments.${index}.image_base64`);
    const prefilledExpiresAt = formHook.getValues(`payments.${index}.expires_at`);
    logVMSClick(VMS_CLICK_EVENTS.PAYMENT_COLLECT_CLICK, "Collect Payment", {
      event_action: paymentMode,
      client_id: customerId,
      order_id: prefilledOrderId,
      amount: parseFloat(amount),
    });
    if (prefilledOrderId) {
      const prefilled = online_payment_details?.find((item) => item?.order_id === prefilledOrderId);
      const prefilledRow = {
        payment_mode: paymentMode,
        amount: amount,
        order_status: "PENDING",
        order_id: prefilledOrderId,
        mobile_number: mobile_number,
        machine_id: machineId,
        image_base64: prefilledQR,
        expires_at: prefilledExpiresAt,
      };
      if (paymentMode === "QR_PAYMENT" && isQRLive(prefilledRow) && parseFloat(prefilled?.amount) === parseFloat(amount)) {
        updateOnlinePaymentMethod(index, prefilledRow);
        showQROnMirror(prefilledRow);
        startPaymentTracking(prefilledOrderId, index, amount, paymentMode, mobile_number, machineId);
        setOnlinePaymentOrderPending(false);
        return;
      }
      deleteOnlinePayment(prefilledOrderId);
    }
    const constructedBody: IPOSProps = {
      amount: parseFloat(amount),
      machine_id: machineId,
      clinic_id: mappedClinicId,
      invoice_ids: invoice_ids,
      payment_mode: paymentMode,
      user_id: customerId,
      source: "INVOICE_PAYMENT",
      mobile_number: paymentMode === "PAYMENT_LINK" ? parseInt(mobile_number) : undefined,
    };
    collectOnlinePayment(constructedBody).then(({ status, message, payment_status, image_base64, expires_at }) => {
      if (status) {
        const row = {
          payment_mode: paymentMode,
          amount: amount,
          order_status: payment_status,
          order_id: message,
          mobile_number: mobile_number,
          machine_id: machineId,
          image_base64: image_base64,
          ...(paymentMode === "QR_PAYMENT" ? { expires_at } : {}),
        };
        updateOnlinePaymentMethod(index, row);
        if (paymentMode === "QR_PAYMENT" && image_base64) showQROnMirror(row);
        startPaymentTracking(message, index, amount, paymentMode, mobile_number, machineId);
        setOnlinePaymentOrderPending(false);
      } else {
        setOnlinePaymentOrderPending(false);
      }
    });
  };

  const handelCancelOnlinePayment = (index: any, order_id: any) => {
    setCancelOnlinePaymentInProgress(true);
    const paymentMode = formHook.getValues(`payments.${index}.payment_mode`);
    const amount = formHook.getValues(`payments.${index}.amount`);
    logVMSClick(VMS_CLICK_EVENTS.PAYMENT_CANCEL_CLICK, "Cancel Payment", {
      event_action: paymentMode,
      client_id: customerId,
      order_id,
      amount: parseFloat(amount),
    });
    const mobile_number = formHook.getValues(`payments.${index}.mobile_number`);
    deleteOnlinePayment(order_id).then(({ status, message }) => {
      if (status) {
        updateOnlinePaymentMethod(index, {
          payment_mode: paymentMode,
          amount: amount,
          order_status: "INITIATED",
          order_id: null,
          mobile_number: mobile_number,
        });
        if (paymentMode === "QR_PAYMENT") sendPaymentQREvent("QR_HIDE");
        setCancelOnlinePaymentInProgress(false);
      } else {
        setCancelOnlinePaymentInProgress(false);
      }
    });
  };

  const isAddMoreOnlinePaymentWithoffile = () => {
    const payments = formHook.watch("payments") ?? [];
    const hasPendingPaymentMethod = payments?.some(
      (item) =>
        item?.order_status?.toLowerCase() === "pending" ||
        (item?.order_status?.toLowerCase() === "initiated" && parseFloat(item?.amount || 0) > 0),
    );
    if (onlinePaymentModeItems?.length === 0) {
      return isSnapmintButton > 0 ? true : false;
    } else {
      if (isSnapmintButton > 0 || hasPendingPaymentMethod) {
        return true;
      } else {
        return false;
      }
    }
  };

  const handelSwitchPaymenttype = () => {
    logVMSClick(VMS_CLICK_EVENTS.OFFLINE_PAYMENT_TOGGLE_CLICK, "Offline Payment", {
      event_action: isOfflinePaymentEnabled ? "OFF" : "ON",
      client_id: customerId,
    });
    if (!isOfflinePaymentEnabled) {
      setIsOfflineConfirmationModal(true);
      totalAmountRemaning();
      totalAmountTopaid();
    } else {
      totalAmountRemaning();
      totalAmountTopaid();
      setIsOfflinePaymentEnabled(false);
      replaceOffilePaymentMethod([]);
    }
  };

  const onSubmit = (data: any, e: React.FormEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!data) {
      console.warn("No data provided. Submission aborted.");
      return;
    }
    if (returnState || refundState || cancelState) {
      return;
    }
    logVMSClick(VMS_CLICK_EVENTS.RECORD_PAYMENT_CLICK, "Record Payment", { client_id: customerId, pet_id: petId });
    setPaymentConfirmation(true);
  };

  const hasPendingPaymentMethod = () => {
    const paymentMethod = formHook.watch(`payments`);
    const hasPendingPaymentMethod = paymentMethod?.some((item) => item?.order_status?.toLowerCase() === "pending");
    return hasPendingPaymentMethod;
  };

  const POS_CASH_MODE = "POS-CASH";

  const getPosCashAdvanceLimit = (): number => {
    const totalToBePaid = totalInvoiceAmountToPaid()?.totalToBePaid ?? 0;
    const usedWallet = parseFloat(formHook.watch("used_amount")) || 0;
    const netDue = totalToBePaid - usedWallet - isSnapmintValue;
    if (netDue <= 0) return 0;
    return Math.ceil(netDue / 500) * 500;
  };

  const isAdvanceBlockedForIndex = (index: number): boolean => {
    if (isAdvanceCollectionEligible !== false) return false;
    const mode = formHook.watch(`payments.${index}.payment_mode`);
    if (mode === POS_CASH_MODE) {
      const enteredAmount = parseFloat(formHook.watch(`payments.${index}.amount`)) || 0;
      return enteredAmount > getPosCashAdvanceLimit();
    }
    return totalAmountRemaning() < 0;
  };

  const isInvalidPaymentAmount = (amt: any) => {
    const amtStr = String(amt || "").trim();
    return !amtStr || amtStr.endsWith(".") || isNaN(parseFloat(amtStr)) || parseFloat(amtStr) === 0;
  };

  // RBAC: CART_CENTRAL_INVOICE_ACTIONS — legacy role: central_ops
  const isCentralOpps = useFeatureAccess("CART_CENTRAL_INVOICE_ACTIONS");

  // Urgent cancel: gated by a hardcoded email allowlist, not RBAC.
  const isUrgentCancelUser = isUrgentCancelAllowedUser(userEmail);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
    >
      {isCartInvoiceLoading ? (
        <TableSkeleton />
      ) : invoiceField?.length > 0 ? (
        <div
          className={clsx(
            "mt-2 scroll-inner !h-auto flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto",
            inoviceList?.length > 4 ? "max-h-[400px]" : "max-h-[520px] sm:max-h-[650px]",
          )}
        >
          <div className="w-full">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                  <tr>
                    {tableHeader.map((key, index) => (
                      <th
                        key={index}
                        scope="col"
                        className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider"
                      >
                        {key.item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoiceField?.map((_cl, index) => {
                    const isFirst = index === 0;
                    const isSingle = invoiceField.length === 1;
                    return (
                      <tr
                        key={index}
                        className={clsx("bg-white", isSingle && "rounded-xl", isFirst && !isSingle && "rounded-t-xl")}
                      >
                        <td
                          className={clsx(
                            "whitespace-nowrap px-2 py-3 text-xs font-normal text-gray-900",
                            isSingle && "rounded-tl-2xl",
                            isFirst && !isSingle && "rounded-tl-2xl",
                          )}
                        >
                          {formHook.watch(`invoices.${index}.invoice_number`)}
                          <div className="flex gap-1 items-center">
                            <RBACFlag actionId="VIEW_INVOICE_ACTION_CTA">
                              <button
                                type="button"
                                className="text-[10px] text-blue-400 font-medium cursor-pointer p-0 m-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  logVMSClick(VMS_CLICK_EVENTS.INVOICE_VIEW_PDF_CLICK, "View PDF", {
                                    client_id: customerId,
                                    invoice_id: formHook.getValues(`invoices.${index}.invoice_id`),
                                  });
                                  window.open(formHook.watch(`invoices.${index}.pdf_url`), "_blank");
                                }}
                              >
                                View PDF
                              </button>
                            </RBACFlag>
                            {[
                              "partially_paid",
                              "paid",
                              "partially_refunded",
                              "partially_returned",
                              "refund_requested",
                              "refund_rejected",
                            ].includes(formHook.watch(`invoices.${index}.status`)?.toLowerCase()) &&
                              formHook.watch(`invoices.${index}.is_return_eligible`) && (
                                <RBACFlag actionId="RETURN_INVOICE_ACTION_CTA">
                                  <button
                                    className="text-[10px] text-blue-400 font-medium relative cursor-pointer p-0 m-0"
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setInvoiceId(formHook.watch(`invoices.${index}.invoice_id`));
                                      setReturnState(true);
                                      setInvoiceType("Return");
                                      logVMSClick(VMS_CLICK_EVENTS.INVOICE_RETURN_CLICK, "Return", {
                                        client_id: customerId,
                                        invoice_id: formHook.getValues(`invoices.${index}.invoice_id`),
                                      });
                                    }}
                                  >
                                    <span className="pl-1">Return</span>
                                    <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                  </button>
                                </RBACFlag>
                              )}
                            {[
                              "partially_paid",
                              "paid",
                              "partially_refunded",
                              "partially_returned",
                              "refund_rejected",
                            ].includes(formHook.watch(`invoices.${index}.status`)?.toLowerCase()) &&
                              formHook.watch(`invoices.${index}.is_refund_eligible`) && (
                                <RBACFlag actionId="REFUND_INVOICE_ACTION_CTA">
                                  <button
                                    className="text-[10px] text-blue-400 font-medium relative cursor-pointer p-0 m-0"
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setInvoiceId(formHook.watch(`invoices.${index}.invoice_id`));
                                      setRefundState(true);
                                      setInvoiceType("Refund");
                                      logVMSClick(VMS_CLICK_EVENTS.INVOICE_REFUND_CLICK, "Refund", {
                                        client_id: customerId,
                                        invoice_id: formHook.getValues(`invoices.${index}.invoice_id`),
                                      });
                                    }}
                                  >
                                    <span className="pl-1">Refund</span>
                                    <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                  </button>
                                </RBACFlag>
                              )}
                            {isUrgentCancelUser &&
                              isUrgentCancelEligibleStatus(formHook.watch(`invoices.${index}.status`)) &&
                              isWithinUrgentCancelWindow(formHook.watch(`invoices.${index}.created_at`)) && (
                                <button
                                  type="button"
                                  className="text-[10px] text-[#F72F03] font-medium relative cursor-pointer p-0 m-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setInvoiceId(formHook.watch(`invoices.${index}.invoice_id`));
                                    setInvoiceNumber(formHook.watch(`invoices.${index}.invoice_number`));
                                    // The session runs against the clinic that raised
                                    // the invoice, whichever clinic the user is logged
                                    // into.
                                    setUrgentCancelClinic(formHook.getValues(`invoices.${index}.clinic_details`));
                                    setIsUrgentCancel(true);
                                    logVMSClick(VMS_CLICK_EVENTS.INVOICE_URGENT_CANCEL_CLICK, "Urgent Cancel", {
                                      client_id: customerId,
                                      invoice_id: formHook.getValues(`invoices.${index}.invoice_id`),
                                    });
                                    setCancelState(false);
                                    setUrgentCancelItemsState(true);
                                  }}
                                >
                                  <span className="pl-1">Urgent Cancel</span>
                                  <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                </button>
                              )}
                            {["voucher_paid"].includes(formHook.watch(`invoices.${index}.status`)?.toLowerCase()) ? (
                              isWithinVoucherPaidCancelWindow(formHook.watch(`invoices.${index}.created_at`)) && (
                              <button
                                type="button"
                                className="text-[10px] text-blue-400 font-medium relative cursor-pointer p-0 m-0"
                                onClick={(e) => {
                                  const invoiceClinicId = formHook.getValues(`invoices.${index}.clinic_details.id`);
                                  const invoiceClinic = formHook.getValues(`invoices.${index}.clinic_details`);
                                  const isSameClinic = formHook.getValues(`invoices.${index}.is_same_clinic`);
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setIsUrgentCancel(false);
                                  logVMSClick(VMS_CLICK_EVENTS.INVOICE_CANCEL_CLICK, "Cancel", {
                                    client_id: customerId,
                                    invoice_id: formHook.getValues(`invoices.${index}.invoice_id`),
                                  });
                                  setInvoiceId(formHook.watch(`invoices.${index}.invoice_id`));
                                  // setOpen(true);
                                  setSelectedInvoice(invoiceClinic);
                                  setCancelState(false);

                                  if (invoiceClinicId === mappedClinicId || isSameClinic) {
                                    setCancelState(true);
                                  } else {
                                    setCancelClinicState(true);
                                    setButtonSource("CANCEL");
                                  }
                                }}
                              >
                                <span className="pl-1">Cancel</span>
                                <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                              </button>
                              )
                            ) : (
                              ["unpaid"].includes(formHook.watch(`invoices.${index}.status`)?.toLowerCase()) && (
                                <RBACFlag actionId="CANCEL_INVOICE_ACTION_CTA">
                                  {(!isUrgentCancelUser ||
                                      isWithinUrgentCancelWindow(formHook.getValues(`invoices.${index}.created_at`))) && (
                                    <InvoiceCancelWindowGate
                                      createdAt={formHook.getValues(`invoices.${index}.created_at`)}
                                      windowSeconds={getInvoiceCancelWindowSeconds({
                                        isUrgentCancelUser,
                                        isCentralOpps,
                                        isSnapmintEligible: !!formHook.getValues(`invoices.${index}.snapmint_meta_data.is_eligible`),
                                      })}
                                    >
                                      <button
                                        type="button"
                                        className="text-[10px] text-blue-400 font-medium relative cursor-pointer p-0 m-0"
                                        onClick={(e) => {
                                          const invoiceClinicId = formHook.getValues(
                                            `invoices.${index}.clinic_details.id`,
                                          );
                                          const invoiceClinic = formHook.getValues(`invoices.${index}.clinic_details`);
                                          const isSameClinic = formHook.getValues(`invoices.${index}.is_same_clinic`);
                                          e.stopPropagation();
                                          e.preventDefault();
                                          setIsUrgentCancel(false);
                                          logVMSClick(VMS_CLICK_EVENTS.INVOICE_CANCEL_CLICK, "Cancel", {
                                            client_id: customerId,
                                            invoice_id: formHook.getValues(`invoices.${index}.invoice_id`),
                                          });
                                          setInvoiceId(formHook.watch(`invoices.${index}.invoice_id`));
                                          setSelectedInvoice(invoiceClinic);
                                          setCancelState(false);
                                          if (invoiceClinicId === mappedClinicId || isSameClinic) {
                                            setCancelState(true);
                                          } else {
                                            setCancelClinicState(true);
                                            setButtonSource("CANCEL");
                                          }
                                        }}
                                      >
                                        <span className="pl-1">Cancel</span>
                                        <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                      </button>
                                      </InvoiceCancelWindowGate>
                                    )}
                                </RBACFlag>
                              )
                            )}
                            {formHook.watch(`invoices.${index}.snapmint_inclusive_remove_membership`) && (
                              <RBACFlag actionId="REMOVE_MEMBERSHIP_ACTION_CTA">
                                <button
                                  type="button"
                                  title="Snapmint EMI not completed: remove the memberships from this invoice"
                                  className="text-[10px] text-[#F72F03] font-medium relative cursor-pointer p-0 m-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setRemoveMembershipInvoice(formHook.getValues(`invoices.${index}`));
                                  }}
                                >
                                  <span className="pl-1">Remove Membership</span>
                                  <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                </button>
                              </RBACFlag>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs font-normal text-gray-900">
                          <button
                            className={`${InvoicestatusBackgroundColor(formHook.watch(`invoices.${index}.status`)?.toLowerCase())} py-1 px-3 rounded-full text-[#000000] capitalize`}
                          >
                            {InvoiceStatusDataTransformed(formHook.watch(`invoices.${index}.status`))}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs font-normal text-gray-900">
                          {formHook.watch(`invoices.${index}.created_at`)
                            ? veticDateFormat(formHook.watch(`invoices.${index}.created_at`))
                            : "NA"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs font-normal text-gray-900">
                          {formHook.watch(`invoices.${index}.clinic_details.name`)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs font-normal text-gray-900">
                          {formHook.watch(`invoices.${index}.invoice_total`)}
                        </td>
                        <td
                          className={clsx(
                            "whitespace-nowrap px-2 py-3 text-xs font-normal",
                            formHook.watch(`invoices.${index}.status`)?.toLowerCase() === "partially_paid"
                              ? "text-[#F72F03] font-semibold"
                              : "text-gray-900",
                          )}
                        >
                          {formHook.watch(`invoices.${index}.due_amount`)}
                        </td>
                        <td
                          className={clsx(
                            "whitespace-nowrap px-2 py-3 text-xs font-normal text-gray-900",
                            isSingle && "rounded-tr-2xl",
                            isFirst && !isSingle && "rounded-tr-2xl",
                          )}
                        >
                          <NumberInput
                            className="text-xs w-full"
                            placeholder="Enter Amount"
                            register={formHook.register(`invoices.${index}.amount_to_be_paid`, {
                              required: false,
                              ...forOnlyNumbersWithDecimal.validations,
                              validate: (value) => {
                                if (typeof value !== "string") {
                                  return "Invalid input";
                                }
                                // Trim leading zeros
                                const trimmedValue = value.replace(/^0+/, "") || "0"; // Default to '0' if empty
                                formHook.setValue(`invoices.${index}.amount_to_be_paid`, trimmedValue);
                                // Validate the amount is less than or equal to `dt`
                                const dt = formHook.getValues(`invoices.${index}.due_amount`);
                                if (parseFloat(trimmedValue) > dt) {
                                  return `Amount cannot exceed ${dt}`;
                                }
                                return true;
                              },
                            })}
                            fieldError={formState.errors.invoices?.[index]?.amount_to_be_paid}
                            errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                          />
                          <p className="text-red-400 text-[10px] mt-1 ml-2  ">
                            {parseFloat(formHook.watch(`invoices.${index}.amount_to_be_paid`)) >
                              formHook.getValues(`invoices.${index}.due_amount`) && "Amount cannot be more than due"}
                          </p>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="text-xs bg-[#F5F5F5] font-medium">
                    <td
                      colSpan={4}
                      className="sticky bottom-0 z-20 text-right px-6 py-3 bg-[#F5F5F5] border-t rounded-bl-2xl"
                    >
                      Total:
                    </td>
                    <td className="sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t">
                      {invoiceField?.length > 0 && totalInvoiceAmount()?.totalInvoice}
                    </td>
                    <td className="sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t">
                      {invoiceField?.length > 0 && totalInvoiceAmount()?.totalDue}
                    </td>
                    <td className="sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t rounded-br-2xl">
                      {invoiceField?.length > 0 && totalInvoiceAmountToPaid()?.totalToBePaid}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        invoiceField?.length === 0 && (
          <div className="h-[300px] bg-white">
            <NoItemFoundComponent
              message=""
              description="No Invoice Found"
              imageURL={NO_CART}
              InputClassName="my-0 h-full"
            />
          </div>
        )
      )}
      {invoiceField?.length > 0 && (
        <div className="mt-2 bg-white px-3 py-3 rounded-lg ">
          <p className="text-sm font-semibold text-[#000000]">Use Wallet Amount</p>
          {rewardCreditAvailable > 0 && (
            <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-xs">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-[#07863d]"
                checked={useRewardCredit}
                onChange={(e) => {
                  setUseRewardCredit(e.target.checked);
                  setInitialWalletAmount(false); // re-run wallet prefill with the new amount
                }}
              />
              Use Reward credit (Rs.{rewardCreditAvailable}, clinic bills only)
            </label>
          )}
          <div className="grid grid-cols-10 my-2 items-center gap-x-20">
            <div className="flex items-center gap-4 col-span-4">
              <p className="text-xs">Available Balance :</p>
              <p className="text-xs font-medium">
                Rs.{usableWalletAmount || 0}
                {rewardCreditUsable > 0 && (
                  <span className="ml-1 text-[10px] text-green-700">(incl. Rs.{rewardCreditUsable} Reward credit)</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-5 col-span-6">
              <p className="flex items-center text-xs">Amount to be Used </p>
              <div className="w-64">
                <NumberInput
                  className="text-xs w-32"
                  placeholder="Enter Amount"
                  disabled
                  maxLength={6}
                  register={formHook.register(`used_amount`, {
                    required: false,
                    ...forOnlyNumbersWithDecimal.validations,
                    validate: (value) => {
                      const trimmedValue = String(value).replace(/^0+/, "") || "0";
                      formHook.setValue(`used_amount`, trimmedValue);
                      if (parseFloat(trimmedValue) > usableWalletAmount) {
                        return `Amount cannot exceed`;
                      }
                      return null;
                    },
                  })}
                  fieldError={formState.errors.used_amount}
                  errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                />
                <p className="text-red-400 text-[9px] absolute ">
                  {parseFloat(formHook.watch(`used_amount`)) > usableWalletAmount &&
                    "Used amount cannot be more than wallet amount "}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <SnapmintIntegration
        emi_meta_data={emi_meta_data}
        setPaidSnapmintInvoices={setPaidSnapmintInvoices}
        setIsSnapmintValue={setIsSnapmintValue}
        setIsSnapmintValueButton={setIsSnapmintValueButton}
        removeSnapmintPaymentMethod={removeSnapmintPaymentMethod}
        orderId={orderId}
        setOrderId={setOrderId}
        order_status={snapmintorder_status}
        setOrder_status={setSnapmintorder_status}
        cancelPaymentInProgress={cancelPaymentInProgress}
      />
      {cancelState && (
        <InvoiceCancellationModal
          open={cancelState}
          setOpen={setCancelState}
          invoiceId={invoiceId}
          source="payment_page"
          isUrgentCancel={isUrgentCancel}
        />
      )}
      {urgentCancelItemsState && (
        <UrgentCancelItemsModal
          open={urgentCancelItemsState}
          setOpen={setUrgentCancelItemsState}
          invoiceId={invoiceId}
          invoiceNumber={invoiceNumber}
          clinicId={urgentCancelClinic?.id}
          clinicName={urgentCancelClinic?.name}
          source="payment_page"
        />
      )}
      {removeMembershipInvoice && (
        <RemoveMembershipModal
          open={!!removeMembershipInvoice}
          setOpen={(value) => !value && setRemoveMembershipInvoice(null)}
          invoice={removeMembershipInvoice}
          onRemoved={() => window.location.reload()}
        />
      )}
      {cancelClinicState && (
        <InvoiceReturnClinicModalComponent
          buttonSource={buttonSource}
          open={cancelClinicState}
          setOpen={setCancelClinicState}
          invoiceId={invoiceId}
          source="payment_page"
          clinic_details={selectedInvoice}
          invoiceType={invoiceType}
          onContinue={() => {
            setCancelClinicState(false);
            setCancelState(true);
          }}
          setIsVisibleMappedClinic={setIsVisibleMappedClinic}
        />
      )}
      {isVisibleMappedClinic && (
        <MappedClinicAndCityPage isCrossVisible={true} setIsVisibleMappedClinic={setIsVisibleMappedClinic} />
      )}
      {isFollowUpModalOpen && (
        <FollowupBookingCreation
          open={isFollowUpModalOpen}
          onClose={() => {
            setIsFollowUpModalOpen(false);
            redirectToInvoiceList();
          }}
          cmbookingRecommendations={cmbookingRecommendations}
          userId={customerId}
        />
      )}
      {returnState && (
        <InvoiceReturnModalComponent
          open={returnState}
          setOpen={setReturnState}
          invoiceId={invoiceId}
          invoiceType={invoiceType}
          source="payment_page"
        />
      )}
      {refundState && (
        <InvoiceRefundModalComponent
          refundState={refundState}
          setRefundState={setRefundState}
          invoiceId={invoiceId}
          invoiceType={invoiceType}
          source="payment_page"
        />
      )}
      {paymentConfirmation && (
        <PaymentConfirmationModal
          open={paymentConfirmation}
          setOpen={setPaymentConfirmation}
          formHook={formHook.getValues()}
          rewardCreditBalance={useRewardCredit ? rewardCreditBalance : 0}
          paidSnapmintInvoices={paidSnapmintInvoices}
          isSnapmintValue={isSnapmintValue}
          totalRemaing={totalAmountRemaning()}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
      {invoiceField?.length > 0 && onlinePaymentModeItems?.length > 0 && (
        <div className={`${!onlinePaymentvalidation && "mb-8"} mt-2 bg-white px-3 py-3 rounded-lg w-full`}>
          <p className="text-sm font-semibold">Online Payment Details</p>
          {isAdvanceCollectionEligible === false && (
            <p className="text-amber-600 text-[12px] font-medium mt-1">
              {(formHook.watch("payments") ?? []).some((f: any) => f.payment_mode === POS_CASH_MODE)
                ? `Advance collection is restricted. POS-CASH is allowed up to ₹${getPosCashAdvanceLimit()} (nearest ₹500).`
                : "Advance collection is not allowed for this customer at this clinic."}
            </p>
          )}
          {paymentFields.map((field, index) => (
            <div key={field.id} className="grid grid-cols-10 items-center gap-4 py-4 border-b w-full gap-x-24">
              <div className="flex col-span-4 items-center gap-4 flex-row">
                <p className="text-xs whitespace-nowrap">Payment Mode</p>
                <div className="flex gap-2">
                  <div className="w-[200px]">
                    <SearchableSelectMenu
                      disabled={
                        formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "completed" ||
                        formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending"
                      }
                      errorMessages={[{ message: "Category is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue(`payments.${index}.payment_mode`, item.id);
                        }
                      }}
                      fieldError={
                        formHook?.formState?.errors?.payments
                          ? formHook?.formState?.errors?.payments[index]
                            ? formHook?.formState?.errors?.payments[index]?.payment_mode
                            : null
                          : null
                      }
                      register={formHook.register(`payments.${index}.payment_mode`, {
                        required: formHook.getValues(`payments.${index}.amount`) > 0,
                      })}
                      selectItems={onlinePaymentModeItems}
                      placeholder="Select Payment Mode"
                      showTooltips={true}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={
                        onlinePaymentModeItems?.filter(
                          (oc) => oc.id === formHook.watch(`payments.${index}.payment_mode`),
                        )[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800 w-20"
                      containerClassName="w-full"
                    />
                  </div>
                  {formHook.watch(`payments.${index}.payment_mode`) === "PAYMENT_LINK" ? (
                    <NumberInput
                      className="text-xs w-[180px]"
                      placeholder="Enter mobile number"
                      maxLength={10}
                      disabled={
                        formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "completed" ||
                        formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending"
                      }
                      register={formHook.register(`payments.${index}.mobile_number`, {
                        required: false,
                        ...forOnlyNumbersWithDecimal.validations,
                        validate: (value) => {
                          // Trim leading zeros
                          const trimmedValue = String(value).replace(/^0+/, "") || "0";
                          formHook.setValue(`payments.${index}.mobile_number`, trimmedValue);
                          return null;
                        },
                      })}
                      fieldError={formState.errors.payments?.[index]?.mobile_number}
                      errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                    />
                  ) : formHook.watch(`payments.${index}.payment_mode`)?.startsWith("POS-") ? (
                    <div className="w-1/2">
                      <SearchableSelectMenu
                        disabled={formHook.watch(`payments.${index}.order_status`) === "INITIATED" ? false : true}
                        errorMessages={[{ message: "", type: "required" }]}
                        onSelectItem={(item) => {
                          if (item) {
                            formHook.setValue(`payments.${index}.machine_id`, item.id);
                          }
                        }}
                        fieldError={
                          formHook?.formState?.errors?.payments
                            ? formHook?.formState?.errors?.payments[index]
                              ? formHook?.formState?.errors?.payments[index]?.machine_id
                              : null
                            : null
                        }
                        register={formHook.register(`payments.${index}.machine_id`)}
                        selectItems={paymentMachineItems}
                        placeholder="Payment Machine"
                        showTooltips={true}
                        showTypedErrors={false}
                        showDropdownIcon={true}
                        defaultSelected={
                          paymentMachineItems?.filter(
                            (oc) => oc.id === formHook.watch(`payments.${index}.machine_id`),
                          )[0]
                        }
                        listBoxClassName="w-full"
                        className="text-gray-800 w-20"
                        containerClassName="w-full"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
              <div className=" col-span-5 gap-6 flex items-center">
                <p className="flex items-center text-xs -ml-1">Amount : </p>
                <div className="">
                  <NumberInput
                    className="text-xs w-32"
                    placeholder="Enter Amount"
                    maxLength={6}
                    disabled={
                      formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "completed" ||
                      formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending"
                    }
                    register={formHook.register(`payments.${index}.amount`, {
                      required: false,
                      ...forOnlyNumbersWithDecimal.validations,
                      onBlur: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handelRowAdd("ROWADD");
                      },
                      validate: (value) => {
                        // Trim leading zeros
                        const trimmedValue = String(value).replace(/^0+/, "") || "0"; // Remove all leading zeros, default to '0' if empty
                        formHook.setValue(`payments.${index}.amount`, trimmedValue);
                        return null;
                      },
                    })}
                    fieldError={formState.errors.payments?.[index]?.amount}
                    errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                  />
                  {isAdvanceCollectionEligible === false &&
                    formHook.watch(`payments.${index}.payment_mode`) === POS_CASH_MODE &&
                    (parseFloat(formHook.watch(`payments.${index}.amount`)) || 0) > getPosCashAdvanceLimit() && (
                      <p className="text-red-500 text-[10px] mt-1">
                        Max allowed: ₹{getPosCashAdvanceLimit()}
                      </p>
                    )}
                </div>
                <div className="flex items-center gap-4">
                  {formHook.watch(`payments.${index}.order_status`) === "INITIATED" && (
                    <button
                      type="button"
                      disabled={
                        !formHook.watch(`payments.${index}.payment_mode`) ||
                        onlinePaymentOrderPending ||
                        isInvalidPaymentAmount(formHook.watch(`payments.${index}.amount`)) ||
                        hasPendingPaymentMethod() ||
                        isAdvanceBlockedForIndex(index) ||
                        Boolean(formState.errors?.payments?.[index]?.amount)
                      }
                      className={`cursor-pointer px-5 py-2 text-white rounded-lg border-transparent text-xs ${
                        !formHook.watch(`payments.${index}.payment_mode`) ||
                        onlinePaymentOrderPending ||
                        isInvalidPaymentAmount(formHook.watch(`payments.${index}.amount`)) ||
                        hasPendingPaymentMethod() ||
                        isAdvanceBlockedForIndex(index) ||
                        Boolean(formState.errors?.payments?.[index]?.amount)
                          ? "bg-gray-300"
                          : "bg-black"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handelCollectPaymentOnline(index);
                      }}
                    >
                      {onlinePaymentOrderPending && selectedPaymentId === index ? (
                        <CircleSpinnerLoader h="20" w="113" />
                      ) : (
                        "Collect Payment"
                      )}
                    </button>
                  )}
                  <div className="flex">
                    {formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "failed" && (
                      <p
                        className={`p-1 px-2 text-[#FF0000] bg-red-100 font-medium rounded-full border-transparent text-xs uppercase`}
                      >
                        {formHook.watch(`payments.${index}.order_status`)}
                      </p>
                    )}
                    {formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "completed" && (
                      <p
                        className={`p-1 px-2 text-[#149463] bg-green-100 font-medium rounded-full border-transparent text-xs uppercase`}
                      >
                        {formHook.watch(`payments.${index}.order_status`)}
                      </p>
                    )}
                    {formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending" && (
                      <p
                        className={`p-1 px-2 text-[#E0A200] bg-yellow-100 font-medium rounded-full border-transparent text-xs uppercase`}
                      >
                        {formHook.watch(`payments.${index}.order_status`)}
                      </p>
                    )}
                  </div>
                  {formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending" && (
                    <div className="relative gap-5 flex items-center pl-2.5">
                      {formHook.watch(`payments.${index}.payment_mode`) === "QR_PAYMENT" &&
                        formHook.watch(`payments.${index}.image_base64`) && (
                          <div className="absolute bottom-full right-0 z-20 mb-2 flex w-max flex-col items-center rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                            <p className="mb-1.5 whitespace-nowrap text-xs font-semibold">Scan & Pay ₹{formHook.watch(`payments.${index}.amount`)}</p>
                            <img src={formHook.watch(`payments.${index}.image_base64`)} alt="Payment QR" className={`h-32 w-32 shrink-0 ${qrExpiresAt && qrSecondsLeft <= 0 ? "opacity-60 blur-[3px]" : ""}`} />
                            {qrExpiresAt && (
                              <p className={`mt-1.5 whitespace-nowrap text-xs font-medium ${qrSecondsLeft > 0 ? "text-gray-600" : "text-red-500"}`}>
                                {qrSecondsLeft > 0
                                  ? `QR expires in ${String(Math.floor(qrSecondsLeft / 60)).padStart(2, "0")}:${String(qrSecondsLeft % 60).padStart(2, "0")}`
                                  : "QR Expired"}
                              </p>
                            )}
                          </div>
                        )}
                      <button
                        type="button"
                        className=" cursor-pointer px-4 py-2 bg-black  text-white rounded-lg border-transparent text-xs "
                        onClick={() => handelOnlinePaymentValidate(index, formHook.watch(`payments.${index}.order_id`))}
                      >
                        {onlinePaymentvalidation && selectedPaymentId === index ? (
                          <CircleSpinnerLoader h="20" w="125" />
                        ) : (
                          "Validate Payment"
                        )}
                      </button>
                      <OnlinePaymentCancelModal
                        handelCancelOnlinePayment={() =>
                          handelCancelOnlinePayment(index, formHook.watch(`payments.${index}.order_id`))
                        }
                        cancelOnlinePaymentInProgress={cancelOnlinePaymentInProgress}
                        index={index}
                        orderId={formHook.watch(`payments.${index}.order_id`)}
                      />
                    </div>
                  )}
                  {(formHook.watch(`payments.${index}.order_status`) === "INITIATED" ||
                    formHook.watch(`payments.${index}.order_status`) === "FAILED") && (
                      <div className="pl-8">
                        <button type="button" className="text-red-500 text-xs font-semibold flex justify-start">
                          <XCircleIcon className="w-5 h-5 cursor-pointer" onClick={() => {
                              logVMSClick(VMS_CLICK_EVENTS.PAYMENT_ROW_REMOVE_CLICK, "Online", { client_id: customerId });
                              removePaymentMethod(index);
                            }}
                          />
                        </button>
                      </div>
                    )}
                </div>
              </div>
            </div>
          ))}
          <div className="grid grid-cols-12 gap-1 items-center justify-between pb-6">
            <div className="col-span-6">
              <button
                type="button"
                className="text-[#0068FF] text-xs font-semibold flex items-center gap-2 my-2 w-fit"
                onClick={() => {
                  logVMSClick(VMS_CLICK_EVENTS.PAYMENT_ROW_ADD_CLICK, "Online", { client_id: customerId });
                  handelRowAdd("ROW");
                }}
              >
                <span className="p-1 h-4 items-center flex bg-[#0068FF] text-white rounded-full ">+</span> Add Another
                Online Payment Mode
              </button>
            </div>
            {!isOfflinePaymentEnabled && (
              <div className="pt-2">
                <div className="flex items-center gap-1 flex-row whitespace-nowrap">
                  <p className="text-sm">Total paid :</p>
                  <p className="text-sm">&nbsp;{totalAmountTopaid()}</p>
                </div>
                <div className="flex items-center gap-3 flex-row whitespace-nowrap">
                  <p className="text-sm">
                    {totalAmountRemaning() < 0 ? "Advance Collection : " : "Remaining : "}
                  </p>
                  <p
                    className={`text-sm ${totalAmountRemaning() < 0 ? "text-green-500" : "text-red-400"}`}
                  >
                    {Math.abs(totalAmountRemaning())}
                  </p>
                </div>
              </div>
            )}
          </div>
          {isDogyWorld ? (
            <button className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-4 mt-2" type="button">
              <p>Enable Offline payment </p>
              <Switch
                checked={isOfflinePaymentEnabled}
                onChange={() => {
                  handelSwitchPaymenttype();
                }}
                className={clsx(
                  isOfflinePaymentEnabled ? "bg-indigo-600" : "bg-gray-200",
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    isOfflinePaymentEnabled ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                  )}
                />
              </Switch>
            </button>
          ) : (
            <RBACFlag actionId="CART_BILLING_OFFLINE_PAYMENT">
              <button className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-5" type="button">
                <p>Enable Offline payment </p>
                <Switch
                  checked={isOfflinePaymentEnabled}
                  onChange={() => {
                    handelSwitchPaymenttype();
                  }}
                  className={clsx(
                    isOfflinePaymentEnabled ? "bg-indigo-600" : "bg-gray-200",
                    "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={clsx(
                      isOfflinePaymentEnabled ? "translate-x-5" : "translate-x-0",
                      "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </button>
            </RBACFlag>
          )}
        </div>
      )}
      <SwitchOnlineToOfflineConfirmationModal
        open={isOfflineConfirmationModal}
        setOpen={setIsOfflineConfirmationModal}
        setIsOfflinePaymentEnabled={setIsOfflinePaymentEnabled}
        isOfflinePaymentEnabled={isOfflinePaymentEnabled}
        addOfflinePaymentMethod={addOfflinePaymentMethod}
        offlineData={offlineData}
      />
      {invoiceField?.length > 0 && (isOfflinePaymentEnabled || onlinePaymentModeItems?.length === 0) && (
        <div
          className={`bg-white px-2 py-2 rounded-lg w-full border border-gray-100 shadow-sm ${offlinePaymentFields?.length > 2 && "max-h-[260px] overflow-y-auto"
            }`}
        >
          <p className="text-sm font-semibold">Offline Payment Details</p>
          {offlinePaymentFields.map((field, index) => (
            <div key={field.id} className="grid grid-cols-10 items-center gap-4 py-4 border-b w-full gap-x-24">
              <div className="flex col-span-4 items-center gap-4 flex-row flex-wrap">
                <p className="text-xs whitespace-nowrap">Payment Mode</p>
                <div className="flex gap-2 flex-wrap">
                  <div className="w-[200px]">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Category is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue(`offline_payments.${index}.payment_mode`, item.title);
                        }
                      }}
                      fieldError={
                        formHook?.formState?.errors?.offline_payments
                          ? formHook?.formState?.errors?.offline_payments[index]
                            ? formHook?.formState?.errors?.offline_payments[index]?.payment_mode
                            : null
                          : null
                      }
                      register={formHook.register(`offline_payments.${index}.payment_mode`, {
                        required: formHook.getValues(`offline_payments.${index}.amount`) > 0,
                      })}
                      selectItems={visiblePaymentOptions}
                      placeholder="Select Payment Mode"
                      showTooltips={true}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={
                        visiblePaymentOptions?.filter(
                          (oc) => oc.title === formHook.watch(`offline_payments.${index}.payment_mode`),
                        )[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800 w-20"
                      containerClassName="w-full"
                    />
                  </div>
                  {isUPIorCard(formHook.watch(`offline_payments.${index}.payment_mode`)) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs whitespace-nowrap">BankRefNo(RRN)</p>
                      <input
                        type="text"
                        placeholder="Enter RRN"
                        className="border border-gray-300 rounded-md px-2 py-1 text-xs w-32"
                        {...formHook.register(`offline_payments.${index}.bank_ref_no`, {
                          required: isUPIorCard(formHook.watch(`offline_payments.${index}.payment_mode`)),
                        })}
                      />
                      {formHook?.formState?.errors?.offline_payments?.[index]?.bank_ref_no && (
                        <span className="text-red-500 text-xs">*</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-5 gap-6 flex items-center">
                <p className="flex items-center text-xs">Amount :</p>
                <div>
                  <NumberInput
                    className="text-xs w-32"
                    placeholder="Enter Amount"
                    maxLength={6}
                    register={formHook.register(`offline_payments.${index}.amount`, {
                      required: false,
                      ...forOnlyNumbersWithDecimal.validations,
                      validate: (value) => {
                        const trimmedValue = String(value).replace(/^0+/, "") || "0";
                        formHook.setValue(`offline_payments.${index}.amount`, trimmedValue);
                        return null;
                      },
                    })}
                    fieldError={formState.errors.offline_payments?.[index]?.amount}
                    errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                  />
                </div>
                <button type="button" className="text-red-500 hover:text-red-600">
                  <XCircleIcon className="w-5 h-5 cursor-pointer" onClick={() => {
                      logVMSClick(VMS_CLICK_EVENTS.PAYMENT_ROW_REMOVE_CLICK, "Offline", { client_id: customerId });
                      removeOfflinePaymentMethod(index);
                    }}
                  />
                </button>
              </div>
            </div>
          ))}
          <div className="grid grid-cols-10 gap-1 items-center justify-between pb-6 mb-10 gap-x-24">
            <div className="col-span-4">
              <button
                type="button"
                className="text-[#0068FF] text-xs font-semibold flex items-center gap-2 my-2 w-fit"
                onClick={() => {
                  logVMSClick(VMS_CLICK_EVENTS.PAYMENT_ROW_ADD_CLICK, "Offline", { client_id: customerId });
                  addOfflinePaymentMethod({ payments_method: null, amount: "0" });
                }}
              >
                <span className="p-1 h-4 items-center flex bg-[#0068FF] text-white rounded-full">+</span>
                Add Another Offline Payment Mode
              </button>
            </div>
            <div className="pt-2">
              <div className="flex items-center gap-1 flex-row whitespace-nowrap">
                <p className="text-sm">Total paid :</p>
                <p className="text-sm">&nbsp;{totalAmountTopaid()}</p>
              </div>
              <div className="flex items-center gap-3 flex-row whitespace-nowrap">
                <p className="text-sm">
                  {totalAmountRemaning() < 0 ? "Advance Collection : " : "Remaining : "}
                </p>
                <p
                  className={`text-sm ${totalAmountRemaning() < 0 ? "text-green-500" : "text-red-400"}`}
                >
                  {Math.abs(totalAmountRemaning())}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        className={`fixed bottom-4 z-20 transition-all duration-300
  ${desktop?.show ? "lg:left-[19rem]" : "lg:left-[6rem]"}
  left-4 right-4 lg:right-6`}
      >
        <div className="p-2">

          <button
            type={!isCartInvoiceLoading && invoiceField?.length === 0 ? "button" : "submit"}
            onClick={(e) => {
              if (!isCartInvoiceLoading && invoiceField?.length === 0) {
                e.preventDefault();
                e.stopPropagation();
                if ((cmbookingRecommendations?.length ?? 0) > 0) {
                  const logBody: IClickLogBody = {
                    event_type: "RECORD_PAYMENT_NO_INVOICE_CLICK",
                    scr_id: "CART_INVOICE_PAYMENT_PAGE",
                    client_id: customerId || "",
                    pet_id: petId || undefined,
                    clinic_id: mappedClinicId || undefined,
                    clinic_name: mappedClinicName || undefined,
                    vms_user_id: userEmail || "",
                    source: "EMR_FOLLOW_UP",
                    timestamp: Date.now(),
                  };
                  setTimeout(() => {
                    logClickAPI(logBody);
                  }, 0);
                  setIsFollowUpModalOpen(true);
                }
              }
            }}
            className={`text-white w-full py-2.5 rounded-lg font-semibold text-sm transition-colors
      ${((!isCartInvoiceLoading && invoiceField?.length === 0 && (cmbookingRecommendations?.length ?? 0) > 0) ||
                (invoiceField?.length > 0 && totalAmountTopaid() > 0 && !isAddMoreOnlinePaymentWithoffile())) &&
                !paymentFields.some((_, i) => isAdvanceBlockedForIndex(i) || isInvalidPaymentAmount(formHook.watch(`payments.${i}.amount`)))
                ? "bg-[#193A6A] hover:bg-[#152f56]"
                : "bg-gray-400 cursor-not-allowed"
              }`}
            disabled={
              isCartInvoiceLoading ||
              (invoiceField?.length === 0 && (cmbookingRecommendations?.length ?? 0) === 0) ||
              (invoiceField?.length > 0 && (totalAmountTopaid() === 0 || isAddMoreOnlinePaymentWithoffile())) ||
              paymentFields.some((_, i) => isAdvanceBlockedForIndex(i) || isInvalidPaymentAmount(formHook.watch(`payments.${i}.amount`)))
            }
          >
            Record Payment
          </button>
        </div>
      </div>
    </form>
  );
};

export default CartInvoiceTableComponent;

const TableSkeleton = () => {
  return (
    <div className="mt-4 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] animate-pulse">
      <div className="w-full overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <table className="w-full min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
            <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-10">
              <tr>
                {[...Array(7)].map((_, index) => (
                  <th key={index} className="p-2 py-3 text-left text-xs font-medium text-gray-300 tracking-wider">
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(6)].map((_, rowIndex) => (
                <tr key={rowIndex} className="bg-white">
                  {[...Array(6)].map((_, colIndex) => (
                    <td key={colIndex} className="px-2 py-3 whitespace-nowrap text-xs font-normal text-gray-300">
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </td>
                  ))}
                  <td className="px-2 py-3 whitespace-nowrap text-xs font-normal text-gray-300">
                    <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const tableHeader = [
  { item: "Invoice ID" },
  { item: "Payment Status" },
  { item: "Created Date & Time" },
  { item: "Clinic" },
  { item: "Total Invoice Amount" },
  { item: "Due Amount" },
  { item: "Amount to be Paid" },
];
